/**
 * Walkability of a zone, one bit of meaning per tile.
 *
 * The server owns the authoritative copy and validates every `move_to` against
 * it (CLAUDE.md rule 1); the client holds an identical copy purely to run
 * pathfinding and predict movement. Both build it from the same Tiled map, so a
 * client that lies about a path is caught on the very first step.
 */
export class CollisionGrid {
    width;
    height;
    /** One entry per tile, row-major. 1 = blocked. */
    blocked;
    constructor(width, height, blocked) {
        if (!Number.isInteger(width) || !Number.isInteger(height) || width <= 0 || height <= 0) {
            throw new RangeError(`Grid size must be positive integers, got ${String(width)}x${String(height)}`);
        }
        if (blocked !== undefined && blocked.length !== width * height) {
            throw new RangeError(`Blocked array holds ${String(blocked.length)} entries, expected ${String(width * height)}`);
        }
        this.width = width;
        this.height = height;
        this.blocked = blocked ?? new Uint8Array(width * height);
    }
    /**
     * Build from ASCII rows, `#` blocked and anything else walkable. Used by
     * tests and by generated maps; Tiled import lands on the same shape.
     */
    static fromRows(rows) {
        const height = rows.length;
        const width = rows[0]?.length ?? 0;
        if (height === 0 || width === 0)
            throw new RangeError('Grid needs at least one row');
        const blocked = new Uint8Array(width * height);
        rows.forEach((row, y) => {
            if (row.length !== width) {
                throw new RangeError(`Row ${String(y)} is ${String(row.length)} wide, expected ${String(width)}`);
            }
            for (let x = 0; x < width; x += 1) {
                if (row[x] === '#')
                    blocked[y * width + x] = 1;
            }
        });
        return new CollisionGrid(width, height, blocked);
    }
    contains(x, y) {
        return (Number.isInteger(x) &&
            Number.isInteger(y) &&
            x >= 0 &&
            y >= 0 &&
            x < this.width &&
            y < this.height);
    }
    /** Outside the map counts as blocked: no special case at the borders. */
    isWalkable(x, y) {
        return this.contains(x, y) && this.blocked[y * this.width + x] === 0;
    }
    isWalkableTile(tile) {
        return this.isWalkable(tile.x, tile.y);
    }
    setBlocked(x, y, value) {
        if (!this.contains(x, y)) {
            throw new RangeError(`Tile (${String(x)},${String(y)}) is outside the ${String(this.width)}x${String(this.height)} grid`);
        }
        this.blocked[y * this.width + x] = value ? 1 : 0;
    }
    /** Walkable tiles orthogonally adjacent to (x, y), in a stable order. */
    neighbours(x, y) {
        const candidates = [
            { x, y: y - 1 },
            { x: x + 1, y },
            { x, y: y + 1 },
            { x: x - 1, y },
        ];
        return candidates.filter((tile) => this.isWalkable(tile.x, tile.y));
    }
    /** Count of blocked tiles; cheap sanity check when loading a map. */
    get blockedCount() {
        let total = 0;
        for (const value of this.blocked)
            if (value === 1)
                total += 1;
        return total;
    }
}
//# sourceMappingURL=collision.js.map